import { List, useModalForm, useTable } from '@refinedev/antd';
import {
  BaseRecord,
  getDefaultFilter,
  IResourceComponentsProps,
} from '@refinedev/core';
import { Button, Menu, MenuProps, Table } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";

import { DeleteOutlined } from '@ant-design/icons';
import { ColumnType } from 'antd/es/table';
import { useModalForm as useModalFormAntd } from 'sunflower-antd';
import { NewBankAccountModalCreate } from '../../components/modal/admin/addBankAccount';
import { EditBankAccount } from '../../components/modal/admin/editBankAccount';
import { useDeleteBankAccount } from '../../utils/hooks/useDeleteBankAccount.hook';
import { useIsAdminRole } from '../../utils/hooks/useIdentityAdmin.hook';
import { useNotificationOkCancel } from '../../utils/hooks/useNotificationOkCancel.hook';
import { onSearch } from '../../utils/list.utils';
import { ButtonListHeader } from '../components/buttonsList';
import { confirmModalDeleteProps } from '../components/constants';
import { Row } from '../components/interface';
import { TableForm } from '../crypto-withdrawal/tableForm';
import '../styles/index.css';

export const BankAccountsList: React.FC<IResourceComponentsProps> = () => {
  const navigate = useNavigate();
  const [fieldData, setFieldData] = useState<BaseRecord>()
  const [deletingId, setDeletingId] = useState<number>()
  const {
    tableProps,
    searchFormProps,
    filters,
    setCurrent,
    tableQueryResult: { refetch: refetchList },
  } = useTable<Row>({
    syncWithLocation: true,
    pagination: {
      pageSize: 10,
    },
    onSearch,
  })
  const isAdminRole = useIsAdminRole()

  const {
    modalProps: createBankAccountModalProps,
    formProps: createBankAccountFormProps,
    show: createBankAccountModalShow,
    formLoading: createBankAccountFormLoading,
    submit: createBankAccountFormSubmit,
    close: createBankAccountModalClose,
  } = useModalForm({
    warnWhenUnsavedChanges: false,
    action: 'create',
  })

  const {
    close: editModalClose,
    modalProps: editModalProps,
    formProps: editFormProps,
    show: editModalShow,
  } = useModalFormAntd({})

  const { deleteBankAccount } = useDeleteBankAccount()

  const reloadListAfterDelete = useCallback(async () => {
    const result = await refetchList()
    const pagination = tableProps.pagination
    if (!pagination || typeof pagination === 'boolean') return

    const responseData: any = result?.data
    const pageSize = Number(pagination.pageSize || 10)
    const currentPage = Number(pagination.current || 1)
    const responseTotal = Number(
      responseData?.total ??
      responseData?.totalCount ??
      responseData?.data?.total ??
      responseData?.data?.totalCount
    )

    const total = Number.isNaN(responseTotal)
      ? Number(pagination.total || 0)
      : responseTotal
    const targetPage = Math.max(1, Math.ceil(total / pageSize))

    if (currentPage > targetPage) {
      if (setCurrent) {
        setCurrent(targetPage)
      } else {
        pagination.onChange?.(targetPage, pageSize)
      }
    }
  }, [refetchList, setCurrent, tableProps.pagination])

  const onOkDelete = useCallback(async () => {
    if (deletingId !== undefined) {
      setDeletingId(undefined)
      await deleteBankAccount(deletingId)
      await reloadListAfterDelete()
    }
  }, [deletingId, deleteBankAccount, reloadListAfterDelete])

  const onCancelDelete = useCallback(() => {
    setDeletingId(undefined)
  }, [])

  const { openNotification, contextHolder } = useNotificationOkCancel({
    ...confirmModalDeleteProps,
    onOk: onOkDelete,
    onCancel: onCancelDelete,
  })

  const handleClickOnCell = (record: BaseRecord) => {
    return {
      onClick: () => {
        if (record.id && isAdminRole) {
          setFieldData(record)
        }
      },
    }
  }

  useEffect(() => {
    if (deletingId !== undefined) {
      openNotification()
    }
  }, [deletingId, openNotification])

  const onClickDelete = (id: number) => () => {
    setDeletingId(id)
  }

  const columns: ColumnType<Row>[] = [
    {
      dataIndex: 'id',
      title: 'ID',
      onCell: handleClickOnCell,
      sorter: true,
    },
    {
      dataIndex: ['bank', 'name'],
      title: 'Bank',
      onCell: handleClickOnCell,
      sorter: true,
    },
    {
      dataIndex: ['bank', 'swift'],
      title: 'SWIFT',
      onCell: handleClickOnCell,
    },
    {
      dataIndex: 'currencyCode',
      title: 'Currency',
      onCell: handleClickOnCell,
    },
    {
      dataIndex: 'accountNumber',
      title: 'Acct No',
      onCell: handleClickOnCell,
    },
    {
      dataIndex: ['company', 'name'],
      title: 'Company',
      onCell: handleClickOnCell,
      sorter: true,
    },
    {
      dataIndex: 'correspondentBank',
      title: 'Corr Bank',
      onCell: handleClickOnCell,
    },
    {
      dataIndex: 'correspondentBankSwift',
      title: 'Bank Corr SWIFT',
      onCell: handleClickOnCell,
    },
    {
      dataIndex: 'correspondentBankAccountNumber',
      title: 'Corr Bank Acct No',
      onCell: handleClickOnCell,
    },
    {
      render: (value, data) => {
        return (
          isAdminRole && (
            <Button
              className={'list-button list-button--delete'}
              icon={<DeleteOutlined style={{ fontSize: '19px' }} />}
              size='small'
              onClick={onClickDelete(data.id)}
            />
          )
        )
      },
    },
  ]
  const defaultValue = 'bank-accounts'

  const [pageType, setPageType] = useState<'customers' | 'bank-accounts'>(defaultValue)
  const FILTER_ITEMS: MenuProps["items"] = [
    {
      label: "Customer Accounts",
      key: 'customers',
    },
    {
      label: "Customer's Bank account",
      key: 'bank-accounts',
    },
  ];

  const changeFilterPageType = (item: any) => {
    const key = item.key as 'customers' | 'bank-accounts'
    setPageType(key)
    navigate(`/${key}?pageSize=10&current=1`);
  }
  return (
    <>
      <List
        // title='Customer Bank Accounts Directory'
        title='Customers'
        headerButtons={
          isAdminRole ? ButtonListHeader(createBankAccountModalShow) : null
        }>
        <TableForm
          {...searchFormProps}
          initialValues={{
            searchValue: getDefaultFilter('', filters),
          }}
          onSubmit={searchFormProps.form?.submit}
          searchPlaceholder={'Search bank accounts by company name'}
          showDateFilter={false}
        />
        <Menu
          onClick={changeFilterPageType}
          defaultSelectedKeys={[pageType]}
          mode="horizontal"
          items={FILTER_ITEMS}
        />
        <Table
          {...tableProps}
          rowKey='id'
          columns={columns as any}
          dataSource={tableProps.dataSource}
        />
      </List>
      {contextHolder}
      <NewBankAccountModalCreate
        closeModal={createBankAccountModalClose}
        formProps={createBankAccountFormProps}
        modalProps={createBankAccountModalProps}
        formIsLoading={createBankAccountFormLoading}
        formSubmit={createBankAccountFormSubmit}
      />
      <EditBankAccount
        fieldData={fieldData}
        changeFieldData={setFieldData}
        show={editModalShow}
        formProps={editFormProps}
        modalProps={editModalProps}
        refetchList={refetchList}
        closeModal={editModalClose}
      />
    </>
  )
}
